// src/hooks/useSessionHeartbeat.ts
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UseSessionHeartbeatOptions {
    intervalMs?: number; // padrão: 30 segundos
    onSessionRevoked?: () => void;
}

export const useSessionHeartbeat = ({
    intervalMs = 120000,
    onSessionRevoked,
}: UseSessionHeartbeatOptions = {}) => {
    const { isAuthenticated, logout, getAccessToken } = useAuth();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const checkSession = useCallback(async () => {
        if (!isAuthenticated) return;

        const token = getAccessToken();
        if (!token) return;

        try {
            const response = await fetch('/api/auth/verify', {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 401) {
                const data = await response.json();

                // Só disparar o modal se for SESSION_REVOKED
                // TOKEN_EXPIRED é tratado pelo fetchWithAuth no AuthContext — não fazer nada aqui
                if (data.code === 'SESSION_REVOKED') {
                    logout(true); // silent: sessão já revogada no servidor
                    onSessionRevoked?.();
                }
                // TOKEN_EXPIRED, TOKEN_INVALID, etc: ignorar — o AuthContext já cuida
            }
        } catch {
            // Ignorar erros de rede
        }
    }, [isAuthenticated, getAccessToken, logout, onSessionRevoked]);

    useEffect(() => {
        if (!isAuthenticated) return;

        intervalRef.current = setInterval(checkSession, intervalMs);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isAuthenticated, checkSession, intervalMs]);
};